import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Popover, PopoverBody, PopoverHeader, UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useHistory } from 'react-router';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { formatBearerToken } from 'Common.functions';
import { popoverModifiers } from 'enums';
import { fetchDataAgnostic } from 'Services/dataApi';

const LOR_NOTIFICATION_POLL_INTERVAL_MS = 30_000;
const MAX_DISPLAYED_NOTIFICATIONS = 30;
const NOTIFICATION_ACTOR_TYPES = new Set(['author', 'program_user', 'system']);
const NOTIFICATION_STATUSES = new Set([
  'sent_to_author',
  'author_opened',
  'in_progress',
  'uploaded',
  'submitted',
  'supplement_required',
  'assigned_to_program',
  'released_to_program',
  'viewed_by_program',
]);

const readStoredTimestamp = (storageKey) => {
  try {
    return localStorage.getItem(storageKey);
  } catch (error) {
    return null;
  }
};

const writeStoredTimestamp = (storageKey, timestamp) => {
  try {
    localStorage.setItem(storageKey, timestamp);
  } catch (error) {
    // Notifications still work for the current session when storage is unavailable.
  }
};

const buildEventKey = (requestId, event) =>
  [requestId, event.Status, event.ActorType, event.CreatedAt].map((value) => `${value || ''}`).join('|');

const getNotificationMessage = (notification) => {
  const authorName = notification.authorName || 'Your recommender';

  switch (notification.status) {
    case 'sent_to_author':
      return `A follow-up request was sent to ${authorName}.`;
    case 'author_opened':
      return `${authorName} opened your letter request.`;
    case 'in_progress':
      return `${authorName} started working on your recommendation.`;
    case 'uploaded':
      return `${authorName} uploaded a recommendation letter.`;
    case 'submitted':
      return `${authorName} submitted a recommendation letter.`;
    case 'supplement_required':
      return `${authorName}'s recommendation requires additional information.`;
    case 'assigned_to_program':
      return `${authorName}'s recommendation was shared with a program.`;
    case 'released_to_program':
      return `${authorName}'s recommendation was released to a program.`;
    case 'viewed_by_program':
      return `${authorName}'s recommendation was viewed by a program.`;
    default:
      return `${authorName}'s recommendation was updated.`;
  }
};

const normalizeNotifications = (requests = []) => {
  return requests
    .flatMap((request) =>
      (request.StatusEvents || [])
        .filter((event) => {
          const actorType = `${event?.ActorType || ''}`.toLowerCase();
          const status = `${event?.Status || ''}`.toLowerCase();
          return NOTIFICATION_ACTOR_TYPES.has(actorType) && NOTIFICATION_STATUSES.has(status) && event?.CreatedAt;
        })
        .map((event) => ({
          key: buildEventKey(request.pk_LoRRequest, event),
          requestId: request.pk_LoRRequest,
          authorName: request.AuthorName,
          status: `${event.Status}`.toLowerCase(),
          actorType: `${event.ActorType}`.toLowerCase(),
          createdAt: event.CreatedAt,
        })),
    )
    .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
    .slice(0, MAX_DISPLAYED_NOTIFICATIONS);
};

const ApplicantLoRNotifications = () => {
  const { getToken, isAuthenticated, user } = useKindeAuth();
  const history = useHistory();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const hasLoadedRef = useRef(false);
  const knownEventKeysRef = useRef(new Set());
  const isPollingRef = useRef(false);
  const isMountedRef = useRef(true);
  const isPopoverOpenRef = useRef(false);
  const abortControllerRef = useRef(null);
  const lastSeenTimestampRef = useRef(null);

  const userStorageId = user?.id || user?.email || 'applicant';
  const lastSeenStorageKey = useMemo(
    () => `applicant_lor_notifications_last_seen_${userStorageId}`,
    [userStorageId],
  );

  const markAllRead = (items = notifications) => {
    const newestTimestamp = items[0]?.createdAt;
    if (newestTimestamp) {
      lastSeenTimestampRef.current = newestTimestamp;
      writeStoredTimestamp(lastSeenStorageKey, newestTimestamp);
    }
    setUnreadCount(0);
  };

  const pollNotifications = useCallback(async () => {
    if (!isAuthenticated || isPollingRef.current || document.hidden) {
      return;
    }

    isPollingRef.current = true;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const token = await getToken();
      const response = await fetchDataAgnostic(
        'applicant/letters/list',
        null,
        formatBearerToken(token),
        { abortController },
      );
      const nextNotifications = normalizeNotifications(response?.data?.requests || []);

      if (!isMountedRef.current) {
        return;
      }

      const nextEventKeys = new Set(nextNotifications.map((notification) => notification.key));
      if (hasLoadedRef.current) {
        const newNotifications = nextNotifications.filter(
          (notification) => !knownEventKeysRef.current.has(notification.key),
        );
        if (newNotifications.length === 1) {
          toast.info(getNotificationMessage(newNotifications[0]));
        } else if (newNotifications.length > 1) {
          toast.info(`${newNotifications.length} new letter updates`);
        }
      }

      let lastSeenTimestamp = lastSeenTimestampRef.current || readStoredTimestamp(lastSeenStorageKey);
      if (!lastSeenTimestamp) {
        lastSeenTimestamp = nextNotifications[0]?.createdAt || new Date().toISOString();
        lastSeenTimestampRef.current = lastSeenTimestamp;
        writeStoredTimestamp(lastSeenStorageKey, lastSeenTimestamp);
      }

      setNotifications(nextNotifications);
      knownEventKeysRef.current = nextEventKeys;
      hasLoadedRef.current = true;
      setHasLoaded(true);

      if (isPopoverOpenRef.current) {
        const newestTimestamp = nextNotifications[0]?.createdAt;
        if (newestTimestamp) {
          lastSeenTimestampRef.current = newestTimestamp;
          writeStoredTimestamp(lastSeenStorageKey, newestTimestamp);
        }
        setUnreadCount(0);
      } else {
        const lastSeenValue = lastSeenTimestamp ? dayjs(lastSeenTimestamp).valueOf() : 0;
        setUnreadCount(
          nextNotifications.filter((notification) => dayjs(notification.createdAt).valueOf() > lastSeenValue).length,
        );
      }
    } catch (error) {
      if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
        // Polling failures are retried without repeatedly interrupting the applicant.
      }
    } finally {
      isPollingRef.current = false;
    }
  }, [getToken, isAuthenticated, lastSeenStorageKey]);

  useEffect(() => {
    abortControllerRef.current?.abort();
    isPollingRef.current = false;
    isMountedRef.current = true;
    hasLoadedRef.current = false;
    knownEventKeysRef.current = new Set();
    lastSeenTimestampRef.current = readStoredTimestamp(lastSeenStorageKey);
    setHasLoaded(false);
    setNotifications([]);
    setUnreadCount(0);
    pollNotifications();

    const intervalId = setInterval(pollNotifications, LOR_NOTIFICATION_POLL_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        pollNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      abortControllerRef.current?.abort();
    };
  }, [lastSeenStorageKey, pollNotifications]);

  const togglePopover = () => {
    const nextIsOpen = !isPopoverOpen;
    isPopoverOpenRef.current = nextIsOpen;
    setIsPopoverOpen(nextIsOpen);
    if (nextIsOpen) {
      markAllRead();
    }
  };

  const openLettersPage = () => {
    markAllRead();
    isPopoverOpenRef.current = false;
    setIsPopoverOpen(false);
    history.push('/applicant/profile/letters');
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        <Button
          id="applicant_lor_notifications_button"
          color="light"
          outline
          aria-label="Letter notifications"
          onClick={togglePopover}
        >
          <FontAwesomeIcon icon={faBell} />
        </Button>
        {unreadCount > 0 ? (
          <Badge
            color="danger"
            pill
            style={{
              position: 'absolute',
              top: -7,
              right: -7,
              minWidth: 20,
              pointerEvents: 'none',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        ) : null}
      </div>
      <UncontrolledTooltip target="applicant_lor_notifications_button">Letter notifications</UncontrolledTooltip>
      <Popover
        isOpen={isPopoverOpen}
        target="applicant_lor_notifications_button"
        placement="bottom"
        modifiers={popoverModifiers}
        toggle={togglePopover}
        style={{ width: 360, maxWidth: 'calc(100vw - 24px)' }}
      >
        <PopoverHeader>Letter Notifications</PopoverHeader>
        <PopoverBody style={{ padding: 0, maxHeight: 420, overflowY: 'auto' }}>
          {!hasLoaded ? (
            <div style={{ padding: 14, color: '#666' }}>Loading notifications...</div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: 14, color: '#666' }}>No letter updates yet.</div>
          ) : (
            notifications.map((notification) => (
              <button
                key={notification.key}
                type="button"
                onClick={openLettersPage}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 14px',
                  border: 0,
                  borderBottom: '1px solid #e3e5e8',
                  backgroundColor: '#fff',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>{getNotificationMessage(notification)}</div>
                <div style={{ marginTop: 3, fontSize: 11, color: '#666' }}>
                  {dayjs(notification.createdAt).format('MMM D, YYYY h:mm A')}
                </div>
              </button>
            ))
          )}
        </PopoverBody>
      </Popover>
    </>
  );
};

export default ApplicantLoRNotifications;
